/**
 * ReceiptsActionModal — Extracted from ImmeublePortal.jsx (Batch #12 refactor).
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Loader2, RefreshCw, Search, ReceiptText, Mail, Download } from "lucide-react";
import { BaseActionModal, API, authH } from "./shared";

export function ReceiptsActionModal({ apartments, buildings, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);  // id du receipt en cours d'action
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [unitId, setUnitId] = useState("");
  const [search, setSearch] = useState("");

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("year", year);
      params.set("limit", 200);
      if (unitId) params.set("unit_id", unitId);
      const r = await axios.get(`${API}/portal/immeuble/receipts?${params.toString()}`,
        { headers: authH() });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Erreur chargement quittances");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, unitId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) =>
      (r.tenant_name || "").toLowerCase().includes(q)
      || (r.property_address || "").toLowerCase().includes(q)
      || (r.receipt_number || "").toLowerCase().includes(q)
      || (r.period_label || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const totalAmount = useMemo(
    () => filtered.reduce((s, r) => s + Number(r.amount || 0), 0),
    [filtered],
  );

  const downloadPdf = async (receipt) => {
    setBusy(receipt.id);
    try {
      const r = await axios.get(
        `${API}/portal/immeuble/receipts/${receipt.id}/pdf`,
        { headers: authH(), responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = receipt.filename || `Quittance_${receipt.receipt_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error("Erreur téléchargement");
    } finally {
      setBusy(null);
    }
  };

  const resendEmail = async (receipt) => {
    if (!receipt.tenant_email) {
      toast.error("Aucune adresse courriel pour ce locataire");
      return;
    }
    setBusy(receipt.id);
    try {
      const r = await axios.post(
        `${API}/portal/immeuble/receipts/${receipt.id}/resend-email`,
        {},
        { headers: authH() },
      );
      if (r.data?.ok) {
        toast.success(`Courriel renvoyé à ${receipt.tenant_email}`);
        fetchReceipts();
      } else {
        toast.error(`Échec : ${r.data?.info || "raison inconnue"}`);
      }
    } catch (e) {
      toast.error("Erreur envoi courriel");
    } finally {
      setBusy(null);
    }
  };

  return (
    <BaseActionModal title="Quittances de loyer" tone="emerald" icon={ReceiptText} onClose={onClose} testId="immeuble-action-modal-receipts">
      <div className="space-y-4">
        {/* Header info */}
        <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <ReceiptText className="w-5 h-5 text-emerald-700 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-emerald-900 leading-relaxed">
            <strong>Génération automatique</strong> : une quittance est émise dès qu&apos;un paiement Stripe est encaissé,
            OU dès que vous approuvez un revenu via la « Conciliation manuelle » côté Admin. Le locataire reçoit
            son reçu par courriel et une copie est conservée dans votre espace sécurisé.
          </div>
        </div>

        {/* Filtres */}
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="text-xs px-3 py-1.5 border border-slate-300 rounded-lg bg-white"
            data-testid="immeuble-receipts-year-select"
          >
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            className="text-xs px-3 py-1.5 border border-slate-300 rounded-lg bg-white"
            data-testid="immeuble-receipts-unit-filter"
          >
            <option value="">Tous les appartements</option>
            {apartments.map((a) => (
              <option key={a.id} value={a.id}>
                {a.building_name || ""} · App. {a.unit_label}
              </option>
            ))}
          </select>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher (locataire, n°, période)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-300 rounded-lg"
              data-testid="immeuble-receipts-search"
            />
          </div>
          <button
            onClick={fetchReceipts}
            className="px-3 py-1.5 text-xs rounded-lg border bg-white hover:bg-slate-50 inline-flex items-center gap-1"
            data-testid="immeuble-receipts-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg border bg-emerald-50/50 p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Émises {year}</div>
            <div className="text-lg font-bold text-emerald-800 font-mono mt-1">{filtered.length}</div>
          </div>
          <div className="rounded-lg border bg-blue-50/50 p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Total encaissé</div>
            <div className="text-lg font-bold text-blue-800 font-mono mt-1">{totalAmount.toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</div>
          </div>
          <div className="rounded-lg border bg-violet-50/50 p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold">Courriels envoyés</div>
            <div className="text-lg font-bold text-violet-800 font-mono mt-1">{filtered.filter((r) => r.email_sent).length}</div>
          </div>
        </div>

        {/* Liste */}
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          {loading ? (
            <div className="p-6 text-center text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
              Chargement...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="immeuble-receipts-empty">
              <ReceiptText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              Aucune quittance pour cette période.
              <div className="text-xs mt-1">Les quittances s'émettent automatiquement après chaque paiement.</div>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="immeuble-receipts-list">
              {filtered.map((r) => (
                <li key={r.id} className="p-3 hover:bg-slate-50 flex items-center gap-3"
                    data-testid={`immeuble-receipt-row-${r.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <ReceiptText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-slate-800">{r.period_label}</span>
                      <span className="text-xs text-slate-400">·</span>
                      <span className="text-xs text-slate-600 truncate">{r.tenant_name || "—"}</span>
                      {r.source === "stripe" && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded">Stripe</span>
                      )}
                      {r.source === "manual_revenue" && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Conciliation</span>
                      )}
                      {r.email_sent ? (
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded inline-flex items-center gap-0.5">
                          <Mail className="w-2.5 h-2.5" /> envoyé
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">no email</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                      <span className="font-mono">{r.receipt_number}</span>
                      <span className="mx-1">·</span>
                      <span>{r.property_address}{r.unit_label ? ` · App. ${r.unit_label}` : ""}</span>
                    </div>
                  </div>
                  <div className="font-mono font-bold text-sm text-emerald-700 whitespace-nowrap mr-2">
                    {Number(r.amount).toFixed(2)} $
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => downloadPdf(r)}
                      disabled={busy === r.id}
                      className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded transition disabled:opacity-40"
                      title="Télécharger PDF"
                      data-testid={`immeuble-receipt-download-${r.id}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => resendEmail(r)}
                      disabled={busy === r.id || !r.tenant_email}
                      className="p-1.5 text-slate-500 hover:text-blue-700 hover:bg-blue-50 rounded transition disabled:opacity-40"
                      title={r.tenant_email ? `Renvoyer à ${r.tenant_email}` : "Aucune adresse courriel"}
                      data-testid={`immeuble-receipt-resend-${r.id}`}
                    >
                      <Mail className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </BaseActionModal>
  );
}
